/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import Redirect from '@modules/routing/Redirect'
import { portalUrl } from '@modules/utils/rootUrls'

import { getSupportedSliderInstanceTypes } from '@/lib/sliders/support'

import { hasUserSettings } from '../../components/UserSettings/helpers'

import { billingUrl, billingUsageUrl, billingOverviewUrl, billingHistoryUrl } from './urls'
import UserconsoleLegacyRoute from './components/UserconsoleLegacyRoute'

import type { RouteObject } from 'react-router-dom'
import type { ReduxState } from '@/types/redux'

export function getRedirects({ state }: { state: ReduxState }): RouteObject[] {
  return [
    {
      path: '/',
      element: (
        <UserconsoleLegacyRoute>
          <Redirect to={portalUrl()} />
        </UserconsoleLegacyRoute>
      ),
    },

    ...getPortalRedirects(),
    ...getStackDeploymentRedirects(),
    ...getAccountRedirects(),
    ...getBillingRedirects(),
    ...getPluginRedirects(),
    ...getSupportRedirects(),
  ]

  function getPortalRedirects(): RouteObject[] {
    if (!hasUserSettings(state)) {
      return []
    }

    return [
      {
        path: '/settings',
        element: <Redirect to='/user/settings' />,
      },
    ]
  }

  function getSupportRedirects(): RouteObject[] {
    return [
      {
        path: '/help',
        element: <Redirect to='/support' />,
      },
    ]
  }

  function getStackDeploymentRedirects(): RouteObject[] {
    return [
      {
        path: '/region/:regionId/deployment/:clusterId',
        element: (
          <Redirect
            to={(params) => `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}`}
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/edit',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/edit`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/elasticsearch',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/elasticsearch`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/elasticsearch/config',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/edit`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/elasticsearch/console',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/elasticsearch/console`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/elasticsearch/logs',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/logs-metrics`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/elasticsearch/snapshots',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/elasticsearch/snapshots`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/elasticsearch/snapshots/:snapshotName',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/snapshots/${params.snapshotName}`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/kibana',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/kibana`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/kibana/edit',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/edit`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/apm',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/apm`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/metrics',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/metrics`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/security',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/security`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/activity',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/activity`
            }
          />
        ),
      },
      ...getSupportedSliderInstanceTypes().map((sliderInstanceType) => ({
        path: `/region/:regionId/deployment/:clusterId/activity/${sliderInstanceType}`,
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/activity/${sliderInstanceType}`
            }
          />
        ),
      })),

      {
        path: '/deployments/:deploymentId/elasticsearch/logs',
        element: <Redirect to={(params) => `/deployments/${params.deploymentId}/logs-metrics`} />,
      },
      {
        path: '/deployments/:deploymentId/monitoring',
        element: <Redirect to={(params) => `/deployments/${params.deploymentId}/health`} />,
      },
    ]
  }

  function getPluginRedirects(): RouteObject[] {
    return [
      {
        path: '/plugins',
        element: <Redirect to='/extensions' />,
      },
      {
        path: '/plugins/create',
        element: <Redirect to='/extensions/create' />,
      },
      {
        path: '/plugins/:pluginId',
        element: <Redirect to={(params) => `/extensions/${params.pluginId}/edit`} />,
      },
      { path: '/deployment-features', element: <Redirect to='/access-security' /> },
      {
        path: '/deployment-features/trust-management',
        element: <Redirect to='/access-security/trust-management' />,
      },
      {
        path: '/deployment-features/traffic-filters',
        element: <Redirect to='/access-security/traffic-filters' />,
      },
      {
        path: '/deployment-features/extensions',
        element: <Redirect to='/extensions' />,
      },
      {
        path: '/deployment-features/extensions/create',
        element: <Redirect to='/extensions/create' />,
      },
      {
        path: '/deployment-features/extensions/:pluginId',
        element: <Redirect to={(params) => `/extensions/${params.pluginId}/edit`} />,
      },
    ]
  }
}

function getAccountRedirects(): RouteObject[] {
  return [
    {
      path: '/account',
      element: <Redirect to='/account/members' />,
    },
    {
      path: '/account/activity',
      element: <Redirect to={billingUsageUrl()} />,
    },
    {
      path: '/account/profile',
      element: <Redirect to='/user/settings' />,
    },
    {
      path: '/account/security',
      element: <Redirect to='/user/settings' />,
    },
  ]
}

function getBillingRedirects(): RouteObject[] {
  const accountRedirect = {
    path: billingUrl(),
    element: <Redirect to={billingOverviewUrl()} />,
  }

  const newBillingUrlsRedirects = [
    {
      path: '/account/usage',
      element: <Redirect to={billingUsageUrl()} />,
    },
    {
      path: '/account/billing',
      element: <Redirect to={billingOverviewUrl()} />,
    },
    {
      path: '/account/billing-history',
      element: <Redirect to={billingHistoryUrl()} />,
    },
    {
      path: '/deployment-pricing-table',
      element: <Redirect to='/cloud-pricing-table' />,
    },
  ]

  return [accountRedirect, ...newBillingUrlsRedirects]
}
